import {
  Box,
  Button,
  Flex,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  MdArrowBack,
  MdChevronLeft,
  MdChevronRight,
  MdDescription,
  MdDownload,
  MdPrint,
} from "react-icons/md";

const FONT_STACK = `'Tajawal','Amiri','Scheherazade New','Noto Sans Arabic','Cairo',sans-serif`;

const C = {
  primary: "#6A1E1E",
  accent: "#4A0F1F",
  pageBg: "#E9E2DB",
  border: "#D8CEC3",
  cream: "#FAF2EB",
  ink: "#1e1b17",
  muted: "rgba(74, 15, 31, 0.45)",
};

function apiBase() {
  const raw = process.env.REACT_APP_API_URL || "https://api.edutopiaonline.com/";
  return String(raw).replace(/\s/g, "").replace(/['"]/g, "").replace(/\/$/, "");
}

/** Direct URL for a lesson PDF path from chapter content (e.g. LessonFiles/…pdf). */
export function lessonPdfPublicUrl(filePath) {
  if (!filePath) return "";
  const base = apiBase();
  const clean = String(filePath).replace(/^\//, "");
  return `${base}/${clean}`;
}

/** Direct URL for a submitted homework file name. */
export function homeworkSubmissionPdfUrl(filename) {
  if (!filename) return "";
  const base = apiBase();
  const raw = String(filename).replace(/^\//, "");
  const path = raw
    .split("/")
    .filter(Boolean)
    .map((seg) => encodeURIComponent(seg))
    .join("/");
  return `${base}/public/homework/${path}`;
}

function stripHash(url) {
  if (!url) return "";
  const i = url.indexOf("#");
  return i >= 0 ? url.slice(0, i) : url;
}

/**
 * When the PDF is on another origin (e.g. api.edutopiaonline.com) the API often sends
 * X-Frame-Options / CSP frame-ancestors that block <iframe src="direct pdf">.
 * Google's viewer fetches the URL server-side and is embeddable from our app.
 * Requires the PDF URL to be publicly reachable (no cookie auth).
 */
function shouldUseGoogleViewer(pdfDirectUrl) {
  if (typeof window === "undefined" || !pdfDirectUrl) return false;
  try {
    const u = new URL(pdfDirectUrl, window.location.href);
    return u.origin !== window.location.origin;
  } catch {
    return true;
  }
}

function googleViewerEmbedUrl(pdfDirectUrl) {
  return `https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(pdfDirectUrl)}`;
}

/**
 * Full-screen style PDF preview (lessons + homework), matching curriculum design.
 *
 * @param {boolean} isOpen
 * @param {() => void} onClose
 * @param {string} pdfUrl — direct .pdf URL
 * @param {string} [title] — document title (optional)
 * @param {string} [backLabel] — default "Back to curriculum"
 * @param {number} [totalPages] — if known; footer shows "PAGE n / total" (direct embed only)
 * @param {"auto"|"direct"|"google"} [embedStrategy] — auto: google viewer when cross-origin
 */
export default function PdfPreviewModal({
  isOpen,
  onClose,
  pdfUrl,
  title,
  backLabel = "Back to curriculum",
  totalPages: totalPagesProp,
  embedStrategy = "auto",
}) {
  const [page, setPage] = useState(1);

  const baseSrc = useMemo(() => stripHash(pdfUrl || ""), [pdfUrl]);

  const useGoogle = useMemo(() => {
    if (!baseSrc) return false;
    if (embedStrategy === "google") return true;
    if (embedStrategy === "direct") return false;
    return shouldUseGoogleViewer(baseSrc);
  }, [baseSrc, embedStrategy]);

  const iframeSrc = useMemo(() => {
    if (!baseSrc) return "";
    if (useGoogle) return googleViewerEmbedUrl(baseSrc);
    return `${baseSrc}#page=${page}`;
  }, [baseSrc, useGoogle, page]);

  useEffect(() => {
    if (isOpen) setPage(1);
  }, [isOpen, baseSrc]);

  const totalPages = Number.isFinite(totalPagesProp) && totalPagesProp > 0 ? totalPagesProp : null;

  const goPrev = useCallback(() => {
    setPage((p) => Math.max(1, p - 1));
  }, []);

  const goNext = useCallback(() => {
    setPage((p) => (totalPages != null ? Math.min(totalPages, p + 1) : p + 1));
  }, [totalPages]);

  const handleDownload = useCallback(() => {
    if (!baseSrc) return;
    const a = document.createElement("a");
    a.href = baseSrc;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.download = "";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [baseSrc]);

  const handlePrint = useCallback(() => {
    if (!baseSrc) return;
    const w = window.open(baseSrc, "_blank", "noopener,noreferrer");
    if (!w) return;
    const tryPrint = () => {
      try {
        w.focus();
        w.print();
      } catch {
        /* cross-origin or blocked */
      }
    };
    setTimeout(tryPrint, 500);
  }, [baseSrc]);

  const pageLabel =
    totalPages != null ? `Page ${page} / ${totalPages}` : `Page ${page}`;

  const iframeKey = useGoogle ? `gview-${baseSrc}` : `${baseSrc}-${page}`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full" motionPreset="none" scrollBehavior="inside">
      <ModalOverlay bg="rgba(74, 15, 31, 0.55)" backdropFilter="blur(8px)" />
      <ModalContent
        display="flex"
        flexDirection="column"
        fontFamily={FONT_STACK}
        color={C.ink}
        bg={C.cream}
        m={{ base: 0, md: 6 }}
        borderRadius={{ base: 0, md: "16px" }}
        borderWidth={{ base: 0, md: "1px" }}
        borderColor={C.border}
        maxW={{ base: "100%", md: "1152px" }}
        h={{ base: "100vh", md: "90vh" }}
        maxH={{ base: "100vh", md: "90vh" }}
        overflow="hidden"
        boxShadow={{ md: "0 4px 20px rgba(0, 0, 0, 0.08)" }}
      >
        <Flex
          as="header"
          align="center"
          justify="space-between"
          flexWrap="wrap"
          gap={3}
          px={{ base: 4, md: 6 }}
          py={4}
          bg={C.pageBg}
          borderBottomWidth="1px"
          borderColor={C.border}
        >
          <Button
            type="button"
            variant="ghost"
            leftIcon={<MdArrowBack size={20} />}
            color={C.primary}
            fontWeight="800"
            fontSize="11px"
            letterSpacing="0.14em"
            textTransform="uppercase"
            onClick={onClose}
            _hover={{ bg: "rgba(106, 30, 30, 0.08)" }}
          >
            {backLabel}
          </Button>
          <Flex align="center" gap={{ base: 2, md: 4 }} flexWrap="wrap">
            <Button
              type="button"
              leftIcon={<MdDownload size={18} />}
              bg={C.primary}
              color="white"
              fontWeight="800"
              fontSize="10px"
              letterSpacing="0.14em"
              textTransform="uppercase"
              borderRadius="md"
              px={5}
              py={2}
              h="auto"
              _hover={{ bg: C.accent }}
              onClick={handleDownload}
            >
              Download
            </Button>
            <IconButton
              aria-label="Print PDF"
              icon={<MdPrint size={22} />}
              variant="ghost"
              color={C.primary}
              _hover={{ bg: "rgba(106, 30, 30, 0.08)" }}
              onClick={handlePrint}
            />
          </Flex>
        </Flex>

        <ModalBody
          p={0}
          flex="1"
          minH={0}
          display="flex"
          flexDirection="column"
          overflow="hidden"
          bg={C.pageBg}
        >
          {title ? (
            <Text px={{ base: 4, md: 6 }} pt={4} pb={2} fontWeight="800" color={C.accent} fontSize="sm" noOfLines={2}>
              {title}
            </Text>
          ) : null}
          <Box flex="1" minH={0} overflow="auto" px={{ base: 3, md: 8 }} py={{ base: 3, md: 6 }}>
            <Box
              maxW="768px"
              mx="auto"
              bg="white"
              borderWidth="1px"
              borderColor={C.border}
              borderRadius="md"
              boxShadow="sm"
              overflow="hidden"
            >
              {iframeSrc ? (
                <iframe
                  key={iframeKey}
                  title={title || "PDF preview"}
                  src={iframeSrc}
                  width="100%"
                  style={{
                    border: "none",
                    minHeight: "65vh",
                    height: "calc(90vh - 220px)",
                    display: "block",
                  }}
                />
              ) : (
                <Flex align="center" justify="center" minH="320px" color={C.muted} fontWeight="600">
                  No document to display.
                </Flex>
              )}
            </Box>
          </Box>
        </ModalBody>

        <ModalFooter
          justifyContent="center"
          flexWrap="wrap"
          gap={{ base: 4, md: 10 }}
          py={4}
          px={{ base: 4, md: 6 }}
          bg={C.pageBg}
          borderTopWidth="1px"
          borderColor={C.border}
        >
          {useGoogle ? (
            <Flex
              align="center"
              gap={2}
              color={C.primary}
              fontWeight="800"
              fontSize="11px"
              letterSpacing="0.18em"
              textTransform="uppercase"
            >
              <MdDescription size={22} />
              <Text as="span">Preview</Text>
            </Flex>
          ) : (
            <>
              <Button
                type="button"
                variant="ghost"
                leftIcon={<MdChevronLeft />}
                color={page <= 1 ? C.muted : C.primary}
                fontWeight="800"
                fontSize="10px"
                letterSpacing="0.12em"
                textTransform="uppercase"
                isDisabled={page <= 1}
                onClick={goPrev}
                _hover={{ color: C.accent, bg: "transparent" }}
              >
                Prev page
              </Button>
              <Flex
                align="center"
                gap={2}
                px={{ base: 4, md: 10 }}
                py={1}
                borderLeftWidth="1px"
                borderRightWidth="1px"
                borderColor={C.border}
                color={C.primary}
                fontWeight="800"
                fontSize="11px"
                letterSpacing="0.18em"
                textTransform="uppercase"
              >
                <MdDescription size={22} />
                <Text as="span">{pageLabel}</Text>
              </Flex>
              <Button
                type="button"
                variant="ghost"
                rightIcon={<MdChevronRight />}
                color={
                  totalPages != null && page >= totalPages ? C.muted : C.primary
                }
                fontWeight="800"
                fontSize="10px"
                letterSpacing="0.12em"
                textTransform="uppercase"
                isDisabled={totalPages != null && page >= totalPages}
                onClick={goNext}
                _hover={{ color: C.accent, bg: "transparent" }}
              >
                Next page
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
